import { useState, useEffect, useMemo } from 'react'
import { useLanguage } from '../context/LanguageContext'
import { useIsDark } from '../hooks/useIsDark'
import { t } from '../i18n'
import { scrollTo } from '../utils/scrollTo'

const sectionIds = ['home', 'about', 'work']

const Sidebar = ({ started = true }) => {
	const { language } = useLanguage()
	const isDark = useIsDark()
	const [activeIndex, setActiveIndex] = useState(0)

	const sections = useMemo(() => sectionIds.map((id) => ({
		id,
		label: t(language, `sidebar.sections.${id}`)
	})), [language])

	useEffect(() => {
		const handleScroll = () => {
			let newIndex = 0
			for (let i = sections.length - 1; i >= 0; i--) {
				const el = document.getElementById(sections[i].id)
				if (!el) continue
				const rect = el.getBoundingClientRect()
				if (rect.top <= window.innerHeight * 0.5) {
					newIndex = i
					break
				}
			}
			setActiveIndex(newIndex)
			if (!started) return
			const newHash = sections[newIndex].id
			if (window.location.hash !== `#${newHash}`) {
				window.history.replaceState(null, '', `#${newHash}`)
			}
		}

		handleScroll()
		window.addEventListener('scroll', handleScroll, { passive: true })
		return () => window.removeEventListener('scroll', handleScroll)
	}, [sections, started])

	return (
		<nav
			aria-label={t(language, 'sidebar.ariaLabel')}
			className='fixed right-4 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-center gap-3 z-50'
		>
			{sections.map((section, i) => (
				<button
					key={section.id}
					aria-label={section.label}
					onClick={() => scrollTo(section.id)}
					className='group relative flex items-center'
				>
					<span className={`pointer-events-none absolute right-full mr-3 whitespace-nowrap rounded-md px-2.5 py-1 text-xs opacity-0 transition-opacity duration-200 group-hover:opacity-100 backdrop-blur-sm border ${
						isDark
							? 'bg-gray-900/90 text-white border-white/10'
							: 'bg-white/90 text-slate-900 border-slate-200'
					}`}>
						{section.label}
					</span>

					<span
						className={`block w-2.5 h-2.5 rounded-full transition-all duration-300 ${
							i === activeIndex
								? isDark
									? 'bg-white scale-125 shadow-[0_0_8px_rgba(255,255,255,0.5)]'
									: 'bg-slate-800 scale-125 shadow-[0_0_8px_rgba(15,23,42,0.3)]'
								: isDark
									? 'bg-white/30 hover:bg-white/60'
									: 'bg-slate-300 hover:bg-slate-500'
						}`}
					/>
				</button>
			))}
		</nav>
	)
}

export default Sidebar
